import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type {
  PermissionsBlockingUsersResponse,
  RoleDeleteBlockingResponse
} from "../redux/permissions.types.ts"
import type {
  PermissionDisplayMap,
  PermissionLabel
} from "../types/permissions.types.ts"
import type {
  CreateLabelData,
  DefaultPermissionsData,
  PermissionLabelId,
  PermissionLabelWithPermissions,
  PermissionUserId,
  ReassignAssignmentsData,
  TransferGccOwnershipData,
  UpdateLabelData
} from "./services.types.ts"

const BASE_URL = "/user"
const BASE_PERMISSIONS_URL = `${BASE_URL}/permissions`

const PATHS = {
  // Labels (Settings -> Permissions)
  labels: `${BASE_PERMISSIONS_URL}/labels/`,
  label: (id: PermissionLabelId) => ({
    get: `${BASE_PERMISSIONS_URL}/labels/${id}/`,
    update: `${BASE_PERMISSIONS_URL}/labels/${id}/`,
    delete: `${BASE_PERMISSIONS_URL}/labels/${id}/`,
    getDefault: `${BASE_PERMISSIONS_URL}/labels/${id}/default/`
  }),
  permissionsDisplay: `${BASE_PERMISSIONS_URL}/display/`,

  // Permission registry
  registry: `${BASE_PERMISSIONS_URL}/registry/`,

  // User management (Team Management)
  user: (userId: PermissionUserId) => ({
    get: `${BASE_URL}/users/${userId}/`,
    manage: `${BASE_URL}/users/${userId}/manage/`,
    reassignAssignments: `${BASE_URL}/${userId}/permissions/reassign-assignments/`
  }),

  // Current user
  myPermissions: `${BASE_PERMISSIONS_URL}/me/`,
  myProfile: `${BASE_PERMISSIONS_URL}/me/`
}

export const getLabels = () =>
  request.api.get<BasePagination<PermissionLabel>, BaseApiResponse<unknown>>(
    PATHS.labels
  )

export const getPermissionsDisplay = () =>
  request.api.get<PermissionDisplayMap, BaseApiResponse<unknown>>(
    PATHS.permissionsDisplay
  )

export const getDefaultPermissions = (labelId: PermissionLabelId) => {
  return request.api.get<DefaultPermissionsData, BaseApiResponse<unknown>>(
    PATHS.label(labelId).getDefault
  )
}

export const getLabelById = (id: PermissionLabelId) =>
  request.api.get<PermissionLabelWithPermissions, BaseApiResponse<unknown>>(
    PATHS.label(id).get
  )

export const createLabel = (data: CreateLabelData) =>
  request.api.post<BaseApiResponse<PermissionLabel>, BaseApiResponse<unknown>>(
    PATHS.labels,
    data
  )

export const updateLabel = (id: PermissionLabelId, data: UpdateLabelData) =>
  request.api.put<
    BaseApiResponse<PermissionLabel>,
    BaseApiResponse<unknown, { blocking?: PermissionsBlockingUsersResponse }>
  >(PATHS.label(id).update, data)

export const deleteLabel = (id: PermissionLabelId) =>
  request.api.delete<
    BaseApiResponse<null>,
    BaseApiResponse<unknown, { blocking?: RoleDeleteBlockingResponse }>
  >(PATHS.label(id).delete)

export const reassignAssignments = (
  userId: PermissionUserId,
  data: ReassignAssignmentsData
) =>
  request.api.post<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.user(userId).reassignAssignments,
    data
  )

export const transferGccOwnership = (data: TransferGccOwnershipData) =>
  request.api.post<BaseApiResponse<void>, BaseApiResponse<unknown>>(
    `${BASE_URL}/permissions/transfer-gcc-ownership/`,
    data
  )
